import React from "react";
import { GlassyModal } from "../glassy-modal/glassy-modal";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { ScanLoadingLinearIndicator } from "@src/components/molecules/scan-loading-linear-indicator/scan-loading-linear-indicator";
import styles from "./ota-firmware-upload-modal.module.scss";
import { OtaDrawerDataInterface } from "@src/types/ota-update";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import WarningTriangleRed from "@src/assets/svg/warning-triangle-red.svg";
import UploadIcon from "@src/assets/svg/upload-icon.svg";
import UploadSuccessIcon from "@src/assets/svg/check-badge.svg";
import CloseIcon from "@src/assets/svg/close-icon.svg";
import { useOtaFirmwareUploadModal } from "./use-ota-firmware-upload-modal";
const cssClassName = "ota-firmware-upload-modal";

const OtaFirmwareUploadModal = ({
  inputValues,
  onClose,
}: {
  inputValues: OtaDrawerDataInterface;
  onClose: () => void;
}) => {
  const { file, texts, firmwareUploadModalState } = useOtaFirmwareUploadModal({
    inputValues,
  });

  const canCloseModal = firmwareUploadModalState !== "firmware-upload-started";
  const onClickOutside = canCloseModal ? onClose : undefined;
  const rightIcon = canCloseModal ? CloseIcon : undefined;
  const rightIconFunction = canCloseModal ? onClose : undefined;

  const OtaFirmwareUploadStartState = () => {
    return (
      <div className={styles[`${cssClassName}__content`]}>
        <div className={styles[`${cssClassName}__title`]}>
          <img src={UploadIcon} alt="Upload" width={25} height={25} />
          <p>{texts.uploadingFirmwareText}</p>
        </div>

        <p className={styles[`${cssClassName}__description`]}>
          {texts.descriptionStartText}
        </p>

        <div className={styles[`${cssClassName}__file-info`]}>
          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.fileText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file?.name}
            </span>
          </div>

          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.sizeText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file ? formatBytes(file.size) : ""}
            </span>
          </div>
        </div>

        <small className={styles[`${cssClassName}__helper-text`]}>
          {texts.helperText}
        </small>

        <div
          className={
            styles[`${cssClassName}__connection-loading-indicator-wrapper`]
          }
        >
          <Spacer16 />
          <Spacer16 />
          <ScanLoadingLinearIndicator />
        </div>
      </div>
    );
  };
  const OtaFirmwareUploadErrorState = () => {
    return (
      <div className={styles[`${cssClassName}__content`]}>
        <div className={styles[`${cssClassName}__title`]}>
          <img src={WarningTriangleRed} alt="Error" width={25} height={25} />
          <p>{texts.firmwareUploadFailedText}</p>
        </div>

        <p className={styles[`${cssClassName}__description`]}>
          {texts.firmwareUploadFailedText}
        </p>

        <div className={styles[`${cssClassName}__file-info`]}>
          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.fileText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file?.name}
            </span>
          </div>

          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.sizeText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file ? formatBytes(file.size) : ""}
            </span>
          </div>
        </div>

        <small className={styles[`${cssClassName}__helper-text`]}>
          {texts.helperFailedText}
        </small>
      </div>
    );
  };
  const OtaFirmwareUploadSuccessState = () => {
    return (
      <div className={styles[`${cssClassName}__content`]}>
        <div className={styles[`${cssClassName}__title`]}>
          <img src={UploadSuccessIcon} alt="Success" width={25} height={25} />
          <p>{texts.firmwareUploadSuccessText}</p>
        </div>

        <p className={styles[`${cssClassName}__description`]}>
          {texts.descriptionSuccessText}
        </p>

        <div className={styles[`${cssClassName}__file-info`]}>
          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.fileText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file?.name}
            </span>
          </div>

          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.sizeText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file ? formatBytes(file.size) : ""}
            </span>
          </div>
        </div>

        <small className={styles[`${cssClassName}__helper-text`]}>
          {texts.descriptionSuccessText}
        </small>
      </div>
    );
  };
  const getModalContent = () => {
    const componentStateByStoreState = {
      "firmware-upload-started": OtaFirmwareUploadStartState,
      "firmware-upload-completed": OtaFirmwareUploadSuccessState,
      "firmware-upload-failed": OtaFirmwareUploadErrorState,
    };

    return componentStateByStoreState[firmwareUploadModalState]();
  };
  return (
    <div className={styles[cssClassName]}>
      <GlassyModal onClickOutside={onClickOutside}>
        <div className={styles[`${cssClassName}__modal-body`]}>
          <GlassyModalHeader
            title={texts.uploadingFirmware}
            rightIcon={rightIcon}
            rightIconFunction={rightIconFunction}
          />
          <HorizontalSeparator verticalMargin={16} />

          <Spacer16 />
          {getModalContent()}
        </div>
      </GlassyModal>
    </div>
  );
};

export default OtaFirmwareUploadModal;
